import {
  MAX_MEETING_RETRY_COUNT,
  MEETING_IN_PROGRESS_REFETCH_INTERVAL_MS,
} from "../constants/meetings";
import type { ICanRetryMeetingParams } from "../types/meetings";
import { EMeetingStatus } from "../types/meetings";

// NOTE: Sets and predicates that classify EMeetingStatus values into
// product-meaningful groups (in-progress / failed / terminal). Lives in
// frontend-shared/utils so web, mobile, and the browser extension can all
// import the same logic — keeps the rules in one place.

// NOTE: Statuses that represent active in-progress work. Useful for the
// "is this still being processed?" check that the UI uses to show pulsing
// badges, hide actions, or group in filters.
export const IN_PROGRESS_MEETING_STATUSES: ReadonlySet<EMeetingStatus> =
  new Set([
    EMeetingStatus.QUEUED,
    EMeetingStatus.PREPARING,
    EMeetingStatus.JOINING_MEETING,
    EMeetingStatus.RECORDING,
    EMeetingStatus.RECORDING_UPLOADING,
    EMeetingStatus.PROCESSING_MEDIA,
    EMeetingStatus.TRANSCRIBING,
    EMeetingStatus.SUMMARIZING,
  ]);

// NOTE: Statuses that represent a terminal failure. Useful for showing a
// retry CTA, error icon, or grouping in filter UIs.
export const FAILED_MEETING_STATUSES: ReadonlySet<EMeetingStatus> = new Set([
  EMeetingStatus.RECORDING_FAILED,
  EMeetingStatus.TRANSCRIPTION_FAILED,
  EMeetingStatus.SUMMARIZATION_FAILED,
  EMeetingStatus.FAILED,
]);

// NOTE: Subset of failure statuses from which the user can manually
// retry the processor pipeline via POST /meetings/:id/retry.
// RECORDING_FAILED is excluded — the live meeting URL session is gone
// and only a brand-new recording can recover. Mirrors
// RETRYABLE_MEETING_STATUSES in server/src/meetings/meetings.constants.ts.
export const RETRYABLE_MEETING_STATUSES: ReadonlySet<EMeetingStatus> = new Set([
  EMeetingStatus.FAILED,
  EMeetingStatus.TRANSCRIPTION_FAILED,
  EMeetingStatus.SUMMARIZATION_FAILED,
]);

// NOTE: Terminal statuses (success, failure, cancellation, or host-denied
// admission). Useful for "is the workflow done?" checks and for stopping
// background polling. CANCELLED and BOT_NOT_ADMITTED are terminal but
// intentionally excluded from FAILED — both are intentional outcomes,
// not errors.
export const TERMINAL_MEETING_STATUSES: ReadonlySet<EMeetingStatus> = new Set([
  EMeetingStatus.PROCESSED,
  EMeetingStatus.CANCELLED,
  EMeetingStatus.BOT_NOT_ADMITTED,
  EMeetingStatus.BOT_LOBBY_TIMEOUT,
  EMeetingStatus.RECORDING_FAILED,
  EMeetingStatus.TRANSCRIPTION_FAILED,
  EMeetingStatus.NO_AUDIO_TRACK,
  EMeetingStatus.NO_SPEECH_DETECTED,
  EMeetingStatus.SUMMARIZATION_FAILED,
  EMeetingStatus.FAILED,
]);

// NOTE: Terminal statuses for which no playable audio can ever arrive, so a
// "still processing" message about one is always wrong (NSAI-4810). This is a
// deliberately narrow subset of TERMINAL_MEETING_STATUSES: the bot never joined
// (BOT_NOT_ADMITTED / BOT_LOBBY_TIMEOUT), the recording itself never came out
// (RECORDING_FAILED), the meeting was called off (CANCELLED), or the uploaded
// file carried no audio track at all (NO_AUDIO_TRACK).
//
// The other failures are excluded ON PURPOSE, because their audio usually
// exists: TRANSCRIPTION_FAILED and SUMMARIZATION_FAILED both mean the recording
// was produced and a *later* stage broke, NO_SPEECH_DETECTED means the audio was
// analysed and simply held no speech, and FAILED is generic enough that assuming
// the worst would hide a recording the user can still play. For those, the
// stream endpoint stays the authority — being told "processing" for a moment and
// then getting the audio beats being told it does not exist.
export const MEETING_STATUSES_WITHOUT_AUDIO: ReadonlySet<EMeetingStatus> =
  new Set([
    EMeetingStatus.RECORDING_FAILED,
    EMeetingStatus.NO_AUDIO_TRACK,
    EMeetingStatus.CANCELLED,
    EMeetingStatus.BOT_NOT_ADMITTED,
    EMeetingStatus.BOT_LOBBY_TIMEOUT,
  ]);

export const hasNoMeetingAudio = (status: EMeetingStatus): boolean =>
  MEETING_STATUSES_WITHOUT_AUDIO.has(status);

export const isMeetingInProgress = (status: EMeetingStatus): boolean =>
  IN_PROGRESS_MEETING_STATUSES.has(status);

// NOTE: NSAI-4812 — the polling policy for the meeting-details query, shared so
// web and mobile cannot drift on how long a stale in-progress screen may live.
// Both clients need it, for different reasons recorded on the constant. Returns
// `false` for every terminal status: those cannot change without a user action
// that already invalidates the query, so the timer must stop rather than poll a
// finished meeting forever. `undefined` (nothing fetched yet) is not in progress.
export const getMeetingRefetchInterval = (
  status: EMeetingStatus | undefined,
): number | false =>
  status && isMeetingInProgress(status)
    ? MEETING_IN_PROGRESS_REFETCH_INTERVAL_MS
    : false;

export const isMeetingFailed = (status: EMeetingStatus): boolean =>
  FAILED_MEETING_STATUSES.has(status);

export const isMeetingCancelled = (status: EMeetingStatus): boolean =>
  status === EMeetingStatus.CANCELLED;

// NOTE: True when the host refused to admit the bot. Distinct from
// isMeetingFailed (no error happened) and from isMeetingCancelled (no
// admin/user action). Used by the meeting-details banner to render the
// neutral grey "host didn't admit the bot" message instead of the red
// recording-failed banner.
export const isMeetingBotNotAdmitted = (status: EMeetingStatus): boolean =>
  status === EMeetingStatus.BOT_NOT_ADMITTED;

export const isMeetingBotLobbyTimeout = (status: EMeetingStatus): boolean =>
  status === EMeetingStatus.BOT_LOBBY_TIMEOUT;

// NOTE: True when the uploaded file had no audio track to transcribe.
// Distinct from isMeetingFailed (NO_AUDIO_TRACK is intentionally excluded
// from FAILED_MEETING_STATUSES) so the meeting-details banner can render a
// dedicated "this file has no audio — upload one with sound" message
// instead of the generic failure copy, and so no Retry button appears
// (re-running the same file would just fail the same way).
export const isMeetingNoAudioTrack = (status: EMeetingStatus): boolean =>
  status === EMeetingStatus.NO_AUDIO_TRACK;

// NOTE: True when the recording was processed but contained no recognizable
// speech (empty transcript). Distinct from isMeetingFailed (NO_SPEECH_DETECTED
// is intentionally excluded from FAILED_MEETING_STATUSES) so the
// meeting-details banner can render a dedicated "no speech was detected —
// nothing to transcribe" message instead of the generic failure copy, and so
// no Retry button appears (re-running the same file just yields the same
// empty transcript). Sibling of isMeetingNoAudioTrack.
export const isMeetingNoSpeechDetected = (status: EMeetingStatus): boolean =>
  status === EMeetingStatus.NO_SPEECH_DETECTED;

// NOTE: NSAI-6193 — terminal errors: the FAILED set plus NO_AUDIO_TRACK and
// NO_SPEECH_DETECTED. The web and mobile Uploads rows offer their actions menu
// on an unprocessed row only for these, so the two cannot drift apart.
export const isMeetingTerminalError = (status: EMeetingStatus): boolean =>
  isMeetingFailed(status) ||
  isMeetingNoAudioTrack(status) ||
  isMeetingNoSpeechDetected(status);

export const isMeetingTerminal = (status: EMeetingStatus): boolean =>
  TERMINAL_MEETING_STATUSES.has(status);

// NOTE: True when the user should see a working Retry button — status
// is retryable AND retry budget remains. Returns false for both
// "wrong status" and "retry budget exhausted" cases; callers that need
// to distinguish those (e.g. to show a "max retries reached" badge)
// should check `RETRYABLE_MEETING_STATUSES.has(status)` and
// `retryCount >= MAX_MEETING_RETRY_COUNT` separately.
export const canRetryMeeting = ({
  status,
  retryCount,
}: ICanRetryMeetingParams): boolean =>
  RETRYABLE_MEETING_STATUSES.has(status) &&
  retryCount < MAX_MEETING_RETRY_COUNT;
